import { el } from 'redom';
import { debounce, isEqual } from 'lodash';
import MD5 from 'crypto-js/md5';
import {
  Canvas as FabricCanvas,
  Image,
  FabricImage,
  Textbox,
  Text,
  Group,
  Rect,
  ActiveSelection,
  Circle,
  Path,
  Control,
  InteractiveFabricObject,
  controlsUtils,
  config,
  FabricObject,
  classRegistry,
  loadSVGFromString,
  util,
  PencilBrush
} from 'fabric';
import { nanoid } from 'nanoid';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import TextToolbar from './toolbars/text';
import ShapeToolbar from './toolbars/shape';
import ImageToolbar from './toolbars/image';
import shapeBuilders from './builders';
// import PlatformImage from './shapes/image';

import CropEditor from './crop-editor';

import {
  getFabricObjectBoundingClientRect,
  loadFonts
} from './utilities';

import audioIconString from 'assets/images/audio-icon.svg?raw';

config.configure({
  initFilterBackend: 'webgl', // or 'canvas' if WebGL is not supported
});

FabricObject.customProperties.push('clipType', 'filterType', 'file', 'videoImage', 'id', 'mediaType', 'mute', 'groupType');
const maxBoxSize = 200;

class Video extends Image {
  static type = 'Video';

  constructor(videoElement, options = {}) {
    super(videoElement, options);
    this.videoElement = videoElement; // The actual video DOM element
  }

  toObject(propertiesToInclude = []) {
    return {
      ...super.toObject(propertiesToInclude),
      src: this.videoElement.src, // Serialize video source
    };
  }

  _render(ctx) {
    if (this.videoElement) {
      ctx.drawImage(
        this.videoElement,
        -this.width / 2,
        -this.height / 2,
        this.width,
        this.height
      );
    }
  }

  static fromObject(object) {

    return new Promise((resolve) => {

      const videoElement = document.createElement('video');
      videoElement.src = object.src;
      videoElement.crossOrigin = 'anonymous';
      videoElement.loop = true;
      videoElement.autoplay = false;
      videoElement.muted = object.mute === true;
      videoElement.onloadeddata = () => {
        videoElement.width = object.width;
        videoElement.height = object.height;
        const video = new Video(videoElement, object);
        video.crossOrigin = 'anonymous';
        video.set(object);
        resolve(video);
      };

    });

  }
}

class Audio extends Image {
  static type = 'Audio';

  constructor(audioIconElement, options = {}) {
    super(audioIconElement, {
      ...options,
      hasControls: false,
      selectable: true,
      lockScalingX: true,
      lockScalingY: true,
      lockRotation: true
    });
    this.url = options.url || ''; // URL of the audio file
    if (this.url !== '') {
      this.audioElement = new window.Audio(this.url);
      this.audioElement.crossOrigin = 'anonymous';
      this.audioElement.preload = 'auto';
    }
  }

  toObject(propertiesToInclude = []) {
    return {
      ...super.toObject(propertiesToInclude),
      url: this.url, // Serialize the audio file URL
    };
  }

  play() {
    if (this.audioElement) {
      this.audioElement.play();
    }
  }

  pause() {
    if (this.audioElement) {
      this.audioElement.pause();
    }
  }

  stop() {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }
  }

  static fromObject(object) {
    return new Promise((resolve) => {
      const audioIconElement = new window.Image();
      audioIconElement.src = object.src; // Image representing the audio (e.g., a speaker icon)
      audioIconElement.onload = () => {
        const audio = new Audio(audioIconElement, object);
        audio.url = object.url;
        audio.set(object);
        resolve(audio);
      };
    });
  }
}

classRegistry.setClass(Video, Video.type);
classRegistry.setClass(Audio, Audio.type);

class Canvas extends EventTarget {

  constructor(element, mediaUploader, onChange, outputType, onReady = (() => {})) {

    super();

    this.configuration = structuredClone(element);
    this.fontFamilies = [...(this.configuration.properties.fontFamilies || [])];
    this.mediaUploader = mediaUploader;
    this.onChange = debounce(onChange, 500);
    this.outputType = outputType;
    this.onReady = onReady;
    this.fabricCanvas = null;
    this.lastActiveObjectId = null;
    this.fontSizeMap = new WeakMap();
    this.keydownEventHandler = null;
    this.deleteKeyDisabled = false;
    this.cropEditor = null;
    this.lastTextboxHeight = null;
    this.lastTextboxWidth = null;
    this.activeTool = null;
    this.isEraserMode = true;

    this.el = el('canvas', {});

  }

  removeGroupsWithBlobImages(canvasData) {

    canvasData.objects = canvasData.objects.filter((obj) => {

      if (obj.type === 'Group') {
        // Check if the group contains any image with a blob URL
        const hasBlobImage = obj.objects.some((child) => {
          return child.type === 'Image' && child.src && child.src.startsWith('blob:');
        });

        // If the group contains a blob image, exclude the group from the data
        return !hasBlobImage;
      }

      // Keep all other objects that are not groups
      return true;
    });

    return canvasData;

  }

  refresh() {

    const scaleFactor = window.devicePixelRatio || 1;

    this.fabricCanvas.setWidth(this.fabricCanvas.getWidth() * scaleFactor);
    this.fabricCanvas.setHeight(this.fabricCanvas.getHeight() * scaleFactor);
    this.fabricCanvas.getContext('2d').scale(scaleFactor, scaleFactor);

  }

  resizeCanvas(newWidth, newHeight) {
    // Set the new canvas width and height without adjusting viewportTransform or scaling objects
    this.fabricCanvas.setWidth(newWidth);
    this.fabricCanvas.setHeight(newHeight);

    // Reset the canvas transformation matrix to remove any scaling or panning
    this.fabricCanvas.viewportTransform = [1, 0, 0, 1, 0, 0];

    // Update each object's coordinates on the canvas to ensure correct positioning
    this.fabricCanvas.getObjects().forEach(object => {
      object.setCoords();
    });

    // Request a render to update the display
    this.fabricCanvas.requestRenderAll();
  }

  deleteSelectedObjects(event) {

    if (this.fabricCanvas === null) {
      return;
    }

    const activeObjects = this.fabricCanvas.getActiveObjects();

    if (activeObjects.length === 0) {
      return;
    }

    if (activeObjects.length === 1) {

      const activeObject = activeObjects[0];

      if (activeObject.type === 'textbox' && activeObject.isEditing) {
        // Let the Textbox handle the Backspace/Delete key
        return;
      }

    }

    event.preventDefault();
    event.stopPropagation();
    activeObjects.forEach((object) => {
      this.fabricCanvas.remove(object);
      this.dispatchEvent(new CustomEvent('removed', { detail: object }));
    });

    this.fabricCanvas.discardActiveObject();

    this.fabricCanvas.renderAll();

  }

  registerKeyDownEvent() {
    this.keydownEventHandler = (event) => {
      const keys = ['Delete', 'Backspace'];
      if (keys.includes(event.key)) {
        if (this.deleteKeyDisabled === false) {
          this.deleteSelectedObjects(event);
        }
      }
    };

    document.addEventListener('keydown', this.keydownEventHandler, { capture: true });

  }

  unregisterKeyDownEvent() {

    document.removeEventListener('keydown', this.keydownEventHandler);

  }

  async loadCanvas() {

    await this.fabricCanvas.loadFromJSON(this.removeGroupsWithBlobImages(this.configuration.properties.data));
    this.fabricCanvas.getObjects().filter((object) => object.type === Audio.type).forEach((object) => {
      object.set({ originX: 'left', originY: 'top', left: 200, top: 200 });
      object.setCoords();
    });
    this.fabricCanvas.backgroundColor = this.configuration.backgroundColor;

    this.resizeCanvas(this.el.closest('.canvas-content').clientWidth, this.el.closest('.canvas-content').clientHeight);

  }

  async onmount() {

    try {

      const fabricCanvas = this.fabricCanvas = new FabricCanvas(this.el, {
        // isDrawingMode: true,
        backgroundColor: '#ffffff',
        selection: false,
        preserveObjectStacking: true,
        skipTargetFind: false,
        renderOnAddRemove: true,
        enableRetinaScaling: true
      });

      await loadFonts(this.fontFamilies);

      this.fabricCanvas.on('mouse:down', function(options) {
        const element = fabricCanvas.getElement();
        element.parentNode.tabIndex = 1000; // Add a tab index to make it focusable
        element.parentNode.focus();
      });

      await this.loadCanvas();

      const tryCacheTextboxWidthHeight = (event) => {

        const selected = event.selected;

        if (selected.length === 1) {
          const selectedObject = selected[0];

          if (selectedObject.type === 'textbox') {
            this.lastTextboxHeight = selectedObject.height;
            this.lastTextboxWidth = selectedObject.width;
          }
        }

      };

      this.fabricCanvas.on('selection:created', tryCacheTextboxWidthHeight);

      this.fabricCanvas.on('selection:updated', tryCacheTextboxWidthHeight);

      this.fabricCanvas.on('object:added', (event) => {
        if (typeof event.target.id !== 'string') {
          event.target.id = nanoid();
        }
        if (event.target.editorControl === true) {
          return;
        }
        this.updateObjectDetails(event.target);
      });

      this.fabricCanvas.on('object:modified', (event) => {
        if (event.target.editorControl === true) {
          return;
        }
        this.updateObjectDetails(event.target);
      });

      this.fabricCanvas.on('object:moving', (event) => {
        if (event.target.editorControl === true) {
          return;
        }
        this.updateObjectDetails(event.target);
      });

      this.fabricCanvas.on('object:scaling', (event) => {
        if (event.target.editorControl === true) {
          return;
        }
        this.updateObjectDetails(event.target);
      });

      this.fabricCanvas.on('object:scaling', (obj) => {

        if (obj.target.type === 'textbox') {
          const textbox = obj.target;
          const controlPoint = textbox.__corner;

          let scaleX = textbox.scaleX;
          let scaleY = textbox.scaleY;

          if (controlPoint) {
            if (controlPoint === 'mb' || controlPoint === 'mt') {
              this.lastTextboxHeight = textbox.height * scaleY;
              scaleY = 1;
              textbox.set({
                height: this.lastTextboxHeight || textbox.height,
                scaleY,
              });
            }
            if (controlPoint === 'mr' || controlPoint === 'ml') {
              this.lastTextboxWidth = textbox.width * scaleX;
              scaleX = 1;
              textbox.set({
                width: this.lastTextboxWidth || textbox.width,
                scaleX
              });
            }
            if (controlPoint === 'br') {
              textbox.set({
                fontSize: textbox.fontSize * scaleX,
              });
            }
          }
          textbox.setCoords();

          fabricCanvas.renderAll();
        }
      });

      this.fabricCanvas.on('text:changed', (event) => {
        this.updateObjectDetails(event.target);
      });

      this.fabricCanvas.on('selection:created', (event) => {

        const activeObject = fabricCanvas.getActiveObject();
        if (activeObject) {
          if (activeObject.editorControl === true) {
            return;
          }
          this.lastActiveObjectId = activeObject.id;
          const configuration = {
            anchor: {
              getBoundingClientRect() {
                return getFabricObjectBoundingClientRect(fabricCanvas, activeObject);
              }
            },
            activeObject,
            fabricCanvas: this.fabricCanvas,
            canvas: this
          };

          TextToolbar.hide(this.el);
          ShapeToolbar.hide(this.el);
          ImageToolbar.hide(this.el);
          if (activeObject.type === 'textbox' || activeObject.type === 'i-text') {
            TextToolbar.show(this.el, configuration);
          } else if (activeObject.type === 'image' || activeObject.groupType === 'image') {
            ImageToolbar.show(this.el, configuration);
          } else {
            ShapeToolbar.show(this.el, configuration);
          }

        } else {
          console.log('Selection updated, object(s) selected.');
        }
      });

      this.fabricCanvas.on('selection:updated', (event) => {

        const activeObject = fabricCanvas.getActiveObject();
        if (activeObject) {
          if (activeObject.editorControl === true) {
            return;
          }
          this.lastActiveObjectId = activeObject.id;
          const configuration = {
            anchor: {
              getBoundingClientRect() {
                return getFabricObjectBoundingClientRect(fabricCanvas, activeObject);
              }
            },
            activeObject,
            fabricCanvas: this.fabricCanvas,
            canvas: this
          };
          ShapeToolbar.hide(this.el);
          TextToolbar.hide(this.el);
          ImageToolbar.hide(this.el);
          if (activeObject.type === 'textbox' || activeObject.type === 'i-text') {
            TextToolbar.show(this.el, configuration);
          } else if (activeObject.type === 'image' || activeObject.groupType === 'image') {
            ImageToolbar.show(this.el, configuration);
          } else {
            ShapeToolbar.show(this.el, configuration);
          }

        } else {
          console.log('Selection updated, object(s) selected.');
        }

      });

      this.fabricCanvas.on('selection:cleared', (event) => {

        this.lastTextboxWeight = null;
        this.lastTextboxHeight = null;

        TextToolbar.hide(this.el);
        ShapeToolbar.hide(this.el);
        ImageToolbar.hide(this.el);

      });

      this.fabricCanvas.on('object:removed', (event) => {

        if (event.target.editorControl === true) {
          return;
        }

        this.updateObjectDetails();

      });

      // this.fabricCanvas.on('path:created', ({ path }) => {

      //   if (!this.activeBrush) return;

      //   const groupBounds = this.activeGroup.getBoundingRect();
      //   const pathBounds = path.getBoundingRect();

      //   // Check if path is inside activeGroup
      //   const isInside = (
      //     pathBounds.left >= groupBounds.left &&
      //     pathBounds.top >= groupBounds.top &&
      //     pathBounds.left + pathBounds.width <= groupBounds.left + groupBounds.width &&
      //     pathBounds.top + pathBounds.height <= groupBounds.top + groupBounds.height
      //   );

      //   this.fabricCanvas.remove(path);
      //   if (!isInside) {
      //     return;
      //   }

      //   const group = this.activeGroup;
      //   path.set({
      //     selectable: false,
      //     evented: false
      //   });
      //   this.activeGroup.add(path);

      //   fabricCanvas.requestRenderAll();
      //   this.updateObjectDetails();

      // });

      // this.fabricCanvas.on('mouse:move', (e) => {

      //   if (!this.activeBrush) return;
      //   if (!this.brushActive) return;
      //   const pointer = this.fabricCanvas.getPointer(e.e);
      //   const imgBounds = this.activeGroup.getBoundingRect();

      //   if (pointer.x >= imgBounds.left && pointer.x <= imgBounds.left + imgBounds.width &&
      //     pointer.y >= imgBounds.top && pointer.y <= imgBounds.top + imgBounds.height) {
      //     this.fabricCanvas.freeDrawingBrush = this.activeBrush;
      //     this.activeBrush.onMouseMove(pointer, e);
      //   } else {
      //     this.fabricCanvas.isDrawingMode = false;
      //     this.brushActive = false;
      //   }
      // });

      // Start drawing on mouse down within the image area
      // this.fabricCanvas.on('mouse:down', (e) => {
      //   if (!this.activeTool) return;

      //   this.activeGroup = this.fabricCanvas.getActiveObject();
      //   this.activeBrush = new PencilBrush(this.fabricCanvas);

      //   this.brushActive = false;
      //   this.fabricCanvas.isDrawingMode = false;
      //   this.fabricCanvas.freeDrawingBrush = null;

      //   const pointer = this.fabricCanvas.getPointer(e.e);
      //   const imgBounds = this.activeGroup.getBoundingRect();

      //   if (pointer.x >= imgBounds.left && pointer.x <= imgBounds.left + imgBounds.width &&
      //     pointer.y >= imgBounds.top && pointer.y <= imgBounds.top + imgBounds.height) {

      //     this.brushActive = true;
      //     this.fabricCanvas.isDrawingMode = true;
      //     this.fabricCanvas.freeDrawingBrush = this.activeBrush;
      //     this.activeGroup.set({
      //       selectable: false,
      //       evented: false
      //     });
      //     this.activeBrush.onMouseDown(pointer, e);
      //   }
      //   this.fabricCanvas.renderAll();
      // });

      this.fabricCanvas.on('mouse:down', (e) => {

        if (this.activeTool === null) {
          return;
        }

        if (this.activeTool.handleCanvasMouseDown) {
          this.activeTool.handleCanvasMouseDown(e, this.fabricCanvas, this);
        }

      });

      this.fabricCanvas.on('mouse:up', (e) => {

        if (this.activeTool === null) {
          return;
        }

        if (this.activeTool.handleCanvasMouseUp) {
          this.activeTool.handleCanvasMouseUp(e, this.fabricCanvas, this);
        }

      });

      this.fabricCanvas.on('mouse:move', (e) => {

        if (this.activeTool === null) {
          return;
        }

        if (this.activeTool.handleCanvasMouseMove) {
          this.activeTool.handleCanvasMouseMove(e, this.fabricCanvas, this);
        }

      });

      this.fabricCanvas.on('path:created', ({ path }) => {

        if (this.activeTool === null) {
          return;
        }

        if (this.activeTool.handleCanvasPathCreated) {
          this.activeTool.handleCanvasPathCreated(path, this.fabricCanvas, this);
        }
        this.updateObjectDetails();

      });

      // this.fabricCanvas.on('mouse:up', (e) => {
      //   this.brushActive = false;
      //   this.fabricCanvas.isDrawingMode = false;
      //   this.fabricCanvas.freeDrawingBrush = null;

      //   if (this.activeBrush) {
      //     this.activeBrush.onMouseUp(e);
      //   }
      //   if (this.activeGroup) {
      //     this.activeGroup.set({
      //       selectable: true,
      //       evented: true
      //     });
      //   }
      //   this.fabricCanvas.renderAll();
      // });

      this.fabricCanvas.selection = true;

      this.registerKeyDownEvent();

      this.onReady(this);
      this.dispatchEvent(new CustomEvent('initialized', { detail: this }));

    } catch (exception) {

      // eslint-disable-next-line no-console
      console.log(exception);

    }

  }

  setActiveTool(tool = null) {

    const currentTool = this.activeTool;

    if (currentTool !== null) {
      currentTool.deactivate();
    }

    this.activeTool = tool;

    if (this.activeTool !== null) {
      this.activeTool.setActiveObject(this.fabricCanvas.getActiveObject());
    }

  }

  async onunmount() {

    try {

      this.fabricCanvas.dispose();
      this.fabricCanvas = null;
      this.unregisterKeyDownEvent();

      TextToolbar.hide(this.el);
      ShapeToolbar.hide(this.el);

      if (this.cropEditor !== null) {
        this.cropEditor.exitCropMode();
        this.cropEditor = null;
      }

    } catch (exception) {

      console.log(exception);

    } finally {
      this.dispatchEvent(new CustomEvent('uninitialized', { detail: this }));
    }

  }

  renderObjects() {
    const objects = this.configuration.properties.data.objects || [];

    objects.forEach(obj => {
      if (obj.type === 'text') {
        const { top, left } = obj.position;
        const { textContent, fontSize, fontFamily, fillColor } = obj.specificProps;

        const textObject = new Textbox(textContent, {
          id: obj.id,
          top,
          left,
          fontSize: fontSize,
          fontFamily: fontFamily,
          fill: fillColor,
        });

        this.fabricCanvas.add(textObject);

      }
    });

    this.fabricCanvas.renderAll();
  }

  getConfiguration() {
    return this.configuration.properties;
  }

  async update(configuration, mediaUploader) {

    const newConfiguration = { ...configuration };
    const currentConfiguration = this.configuration;

    this.configuration = configuration;
    this.mediaUploader = mediaUploader;
    this.fabricCanvas.backgroundColor = configuration.backgroundColor;

    this.fabricCanvas.backgroundColor = configuration.backgroundColor;

    this.resizeCanvas(this.el.closest('.canvas-content').clientWidth, this.el.closest('.canvas-content').clientHeight);

    delete newConfiguration.properties.player;

    if (isEqual(newConfiguration.properties, currentConfiguration.properties) === false) {
      await this.fabricCanvas.loadFromJSON(this.removeGroupsWithBlobImages(this.configuration.properties.data));
      this.fabricCanvas.renderAll();
    }

  }

  clearCanvas() {
    this.fabricCanvas.clear();
  }

  updateObjectDetails() {

    const parentEntityType = this.configuration.properties.parentEntityType;
    const parentEntityId = this.configuration.properties.parentEntityId;

    const data = {
      ...this.configuration.properties,
      data: this.fabricCanvas.toJSON(['clipType', 'filterType', 'file']),
      width: this.fabricCanvas.getWidth(),
      height: this.fabricCanvas.getHeight(),
      fontFamilies: this.fontFamilies
    };
    const previousURL = data.url;
    delete data.url;
    const merkleRoot = MD5(JSON.stringify(data)).toString();
    const url = serverInterface.startup.getFullURL(`/canvases/${parentEntityType}:${parentEntityId}:${merkleRoot}/${this.outputType}`).toString();

    if (previousURL === url) {
      return;
    }

    const newProperties = {
      ...data,
      url
    };
    delete newProperties.player; // Remove player properties 
    this.configuration.properties = newProperties;

    this.onChange(newProperties);
  }

  centerShape(shape) {

    this.fabricCanvas.requestRenderAll();
    this.fabricCanvas.add(shape);
    this.fabricCanvas.centerObject(shape);
    this.fabricCanvas.setActiveObject(shape);
    shape.setCoords();

    this.fabricCanvas.requestRenderAll();

  }

  /**
   * 
   * @param {string} shape 
   * 
   * Inserts a shape into the middle of the canvas with default dimensions
   * Example: 
   * 1. If rectangle is passed, a rectangle with 2px black border 30px width and 30px height will be inserted
   * 2. If circle is passed, a circle with 2px black border 30px radius will be inserted
   * 3. and so on
   * 
   * */
  insertShape(shape) {

    const builder = shapeBuilders[shape];

    const shapeObject = builder(this.fabricCanvas);

    this.centerShape(shapeObject);

    this.dispatchEvent(new CustomEvent('added', { detail: shapeObject }));

  }

  createUploaderBox(img, dimensions) {

    const canvas = this.fabricCanvas;

    const uploadText = new Text('Uploading 0%', {
      left: dimensions.left,
      top: dimensions.top,
      fontSize: 16,
      fill: 'white',
      originX: 'center',
      originY: 'center'
    });

    uploadText.editorControl = true;

    const uploadGroup = new Group([img, uploadText], {
      left: canvas.width / 2,
      top: canvas.height / 2,
      originX: 'center',
      originY: 'center'
    });

    uploadGroup.editorControl = true;

    canvas.add(uploadGroup);
    canvas.centerObject(uploadGroup);
    canvas.renderAll();

    return [uploadGroup, uploadText];

  }

  async buildImageFromFile(source, file) {

    const uploadedImg = await Image.fromURL(file.url, {
      crossOrigin: 'anonymous'
    });

    uploadedImg.crossOrigin = 'anonymous';
    uploadedImg.set({
      file: { _id: file._id, url: file.url }
    });

    const imageGroup = new Group([uploadedImg], {
      scaleX: source.scaleX,
      scaleY: source.scaleY,
      left: source.left,
      top: source.top,
      originX: 'center',
      originY: 'center',
      groupType: 'image'
    });

    return imageGroup;

  }

  async buildVideoFromFile(source, file) {

    return new Promise((resolve) => {

      const videoElement = document.createElement('video');
      videoElement.src = file.url;
      videoElement.autoplay = false;
      videoElement.loop = true;
      videoElement.crossOrigin = 'anonymous';
      videoElement.muted = source.mute === true;

      videoElement.onloadeddata = () => {
        videoElement.width = videoElement.videoWidth;
        videoElement.height = videoElement.videoHeight;
        const video = new Video(videoElement, {
          left: source.left,
          top: source.top,
          scaleX: source.scaleX,
          scaleY: source.scaleY,
        });
        video.crossOrigin = 'anonymous';
        video.set({
          scaleX: source.scaleX,
          scaleY: source.scaleY,
          left: source.left,
          top: source.top,
          originX: 'center',
          originY: 'center',
          file: { _id: file._id, url: file.url },
          videoImage: true
        });
        resolve(video);
      };

    });

  }

  async uploadFile(file, img, scale, dimensions, buildShapeFromFile) {

    const canvas = this.fabricCanvas;
    const [uploadGroup, uploadText] = this.createUploaderBox(img, dimensions);

    const request = await this.mediaUploader(file);

    return new Promise((resolve, reject) => {

      request.on('progress', (data) => {
        uploadText.set('text', `Uploading ${Math.floor(data.percent)}%`);
        canvas.renderAll();
      });

      request.on('success', async (files) => {
        const file = files[0];
        const uploadedShape = await buildShapeFromFile(file);

        canvas.remove(uploadGroup);
        this.centerShape(uploadedShape);
        resolve(uploadedShape);
      });

      request.on('error', () => {
        canvas.remove(uploadGroup);
        canvas.renderAll();

        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: 'Failed to upload file! Try again later!',
          seconds: 5,
          type: 'error'
        });
        reject();
      });

    });

  }

  async insertImage(file) {

    const canvas = this.fabricCanvas;

    const img = await Image.fromURL(URL.createObjectURL(file));

    img.crossOrigin = 'anonymous';
    img.editorControl = true;
    const scale = Math.min(maxBoxSize / img.width, maxBoxSize / img.height);

    const dimensions = {
      scaleX: scale,
      scaleY: scale,
      originX: 'center',
      originY: 'center',
      left: canvas.width / 2,
      top: canvas.height / 2
    };

    img.set(dimensions);

    const image = await this.uploadFile(file, img, scale, dimensions, this.buildImageFromFile.bind(this, img));

    this.dispatchEvent(new CustomEvent('added', { detail: image }));

  }

  async insertVideo(file) {

    const canvas = this.fabricCanvas;
    const videoElement = document.createElement('video');
    videoElement.src = URL.createObjectURL(file);
    videoElement.loop = true; // Set to true to loop
    videoElement.crossOrigin = 'anonymous';

    videoElement.onloadeddata = async () => {
      const width = videoElement.width = videoElement.videoWidth;
      const height = videoElement.height = videoElement.videoHeight;
      const scale = Math.min(maxBoxSize / width, maxBoxSize / height);

      const video = new Video(videoElement, {
        objectCaching: false,
      });

      video.crossOrigin = 'anonymous';
      video.editorControl = true;

      const dimensions = {
        scaleX: scale,
        scaleY: scale,
        originX: 'center',
        originY: 'center',
        left: canvas.width / 2,
        top: canvas.height / 2
      };

      video.set(dimensions);
      const videoShape = await this.uploadFile(file, video, scale, dimensions, this.buildVideoFromFile.bind(this, video));
      this.dispatchEvent(new CustomEvent('added', { detail: videoShape }));
    };

  }

  async buildAudioFromFile(source, file) {

    const url = `/static-file-proxy?url=${encodeURIComponent(file.url)}`;
    const audioElement = new window.Audio(url);
    audioElement.crossOrigin = 'anonymous';

    source.set({
      file: { _id: file._id, url: file.url },
      url,
      audioElement,
      top: 0,
      left: 0
    });

    source.setCoords();

    return source;

  }

  async insertAudio(file) {

    return new Promise(async (resolve, reject) => {

      const audioIcon = new window.Image();
      audioIcon.src = '/assets/images/speaker-icon.svg';
      audioIcon.onload = async () => {
        const audioShape = new Audio(audioIcon, {
          url: '',
          left: 0,
          top: 0,
          width: 24,
          height: 24,
        });
        const scale = 1;

        const dimensions = {
          scaleX: scale,
          scaleY: scale,
          originX: 'center',
          originY: 'center',
          left: 0,
          top: 0
        };

        audioShape.set(dimensions);

        const audio = await this.uploadFile(file, audioShape, scale, dimensions, this.buildAudioFromFile.bind(this, audioShape));

        audio.set({
          left: 40,
          top: 40,
          originX: 'center',
          originY: 'center'
        });
        audio.setCoords(); // Recalculate audioect coordinates
        this.fabricCanvas.renderAll();

        resolve(audio);

        this.dispatchEvent(new CustomEvent('added', { detail: audio }));

      };
    });

  }

  async insertImageAtPosition(file, position) {

    return new Promise(async (resolve, reject) => {

      const canvas = this.fabricCanvas;

      // Load the image from file
      const img = await Image.fromURL(URL.createObjectURL(file));

      img.crossOrigin = 'anonymous';

      const dimensions = {
        ...position
      };

      // img.set(dimensions);

      // Create upload progress text and overlay it on the image
      const uploadText = new Text('Uploading 0%', {
        fontSize: 16,
        fill: 'white',
        originX: 'center',
        originY: 'center',
        left: img.width / 2,
        top: img.height / 2
      });

      const uploadGroup = new Group([img, uploadText], {
        left: dimensions.left,
        top: dimensions.top
      });

      uploadGroup.editorControl = true;

      canvas.add(uploadGroup);
      canvas.renderAll();

      const request = await this.mediaUploader(file);

      request.on('progress', (data) => {
        uploadText.set('text', `Uploading ${Math.floor(data.percent)}%`);
        canvas.renderAll();
      });

      request.on('success', async (files) => {
        const file = files[0];
        const imageUrl = file.url;
        const uploadedImg = await Image.fromURL(imageUrl, {
          crossOrigin: 'anonymous'
        });

        uploadedImg.crossOrigin = 'anonymous';
        uploadedImg.set({
          file: { _id: file._id, url: file.url }
        });

        const imageGroup = new Group([uploadedImg], {
          left: dimensions.left,
          top: dimensions.top,
          groupType: 'image'
        });

        canvas.remove(uploadGroup);
        canvas.add(imageGroup);
        canvas.setActiveObject(imageGroup);
        canvas.requestRenderAll();
        resolve(imageGroup);
      });

      request.on('error', () => {
        canvas.remove(uploadGroup);
        canvas.renderAll();

        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: 'Failed to upload file! Try again later!',
          seconds: 5,
          type: 'error'
        });
        reject();
      });

    });

  }

  async replaceImage(file, image) {

    this.fabricCanvas.remove(image);

    return this.insertImage(file);

  }

  async replaceImageWithURL(url, image) {

    const uploadedImg = await Image.fromURL(url);

    uploadedImg.crossOrigin = 'anonymous';
    const scale = Math.min(maxBoxSize / uploadedImg.width, maxBoxSize / uploadedImg.height);

    uploadedImg.set({
      file: { url }
    });

    const imageGroup = new Group([uploadedImg], {
      scaleX: scale,
      scaleY: scale,
      left: image.left,
      top: image.top,
      groupType: 'image'
    });

    this.fabricCanvas.remove(image);
    this.fabricCanvas.add(imageGroup);
    this.fabricCanvas.renderAll();

  }

  // places new object in active object place, deletes active object & selects new object
  replaceActiveObject(newObject) {

    const activeObject = this.fabricCanvas.getActiveObject();

    if (activeObject) {
      const newObjectOptions = {
        left: activeObject.left,
        top: activeObject.top
      };
      newObject.setOptions(newObjectOptions);

      this.fabricCanvas.remove(activeObject);
      this.fabricCanvas.add(newObject);
      this.fabricCanvas.setActiveObject(newObject);
      this.fabricCanvas.renderAll();
    }

  }

  deleteActiveObject() {

    const activeObject = this.fabricCanvas.getActiveObject();

    if (activeObject) {
      this.fabricCanvas.remove(activeObject);
      this.fabricCanvas.renderAll();
    }

  }

  tryDeleteActiveObject(event) {

    const activeObject = this.fabricCanvas.getActiveObject();

    if (!activeObject) {
      return;
    }

    if (activeObject.type === 'textbox' && activeObject.isEditing) {
      // Let the Textbox handle the Backspace/Delete key
      return;
    } else {
      event.preventDefault();
      event.stopPropagation();
      this.deleteActiveObject();
    }

  }

  async duplicateObject(object) {

    const clonedObject = await object.clone();
    clonedObject.id = nanoid();
    clonedObject.setOptions({ left: object.left + 10, top: object.top + 10 });
    this.fabricCanvas.add(clonedObject);
    this.fabricCanvas.setActiveObject(clonedObject);
    clonedObject.setCoords();
    this.fabricCanvas.renderAll();

    return clonedObject;

  }

  async duplicateActiveObject() {

    const activeObjects = this.fabricCanvas.getActiveObjects();

    if (activeObjects.length > 0) {

      this.fabricCanvas.discardActiveObject();

      const clonedObjects = await Promise.all(activeObjects.map(this.duplicateObject.bind(this)));

      if (clonedObjects.length > 1) {
        const activeSelection = new ActiveSelection(clonedObjects, {
          canvas: this.fabricCanvas
        });
        this.fabricCanvas.setActiveObject(activeSelection);
      } else {
        this.fabricCanvas.setActiveObject(clonedObjects[0]);
      }

    }

  }

  deselectAll() {

    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.renderAll();

  }

  selectLastActiveObject() {

    const activeObject = this.fabricCanvas.getObjects().find(obj => obj.id === this.lastActiveObjectId);

    if (activeObject) {
      this.fabricCanvas.setActiveObject(activeObject);
      this.fabricCanvas.renderAll();
    }

  }

  setClipToSquare(activeObject) {

    activeObject.set({
      clipType: 'square',
      clipPath: new Rect({
        width: activeObject.width, // Adjust based on desired rectangle size
        height: activeObject.height,
        originX: 'center',
        originY: 'center'
      })
    });

  }

  setClipToCircle(activeObject) {

    const boundingRect = activeObject.getBoundingRect();
    const { width, height } = boundingRect;
    const radius = Math.min(width, height) / 2;

    activeObject.set({
      clipType: 'circle',
      clipPath: new Circle({
        radius: radius,
        originX: 'center',
        originY: 'center',
        left: 0,
        top: 0,
        scaleX: 1 / activeObject.scaleX,
        scaleY: 1 / activeObject.scaleY
      })
    });

  }

  setClipToTriangle(activeObject) {

    const trianglePath = 'M 50 0 L 100 100 L 0 100 z';

    activeObject.set({
      clipType: 'triangle',
      clipPath: new Path(trianglePath, {
        left: 0,
        top: 0,
        scaleX: activeObject.width / 100, // Scale triangle path to match image size
        scaleY: activeObject.height / 100,
        originX: 'center',
        originY: 'center'
      })
    });

  }

  setClipToStar(activeObject) {

    const starPath = 'M 50 0 L 61 35 L 98 35 L 68 57 L 79 91 L 50 70 L 21 91 L 32 57 L 2 35 L 39 35 z';

    activeObject.set({
      clipType: 'star',
      clipPath: new Path(starPath, {
        left: 0,
        top: 0,
        scaleX: activeObject.width / 100, // Scale star path to match image size
        scaleY: activeObject.height / 100,
        originX: 'center',
        originY: 'center'
      })
    });

  }

  static configureControls(startup) {

    const colors = startup.theme.colors;

    InteractiveFabricObject.ownDefaults = {
      ...InteractiveFabricObject.ownDefaults,
      cornerSize: 5,
      cornerStrokeColor: colors.tertiary,
      cornerColor: colors.tertiary,
      cornerStyle: 'rect',
      padding: 7,
      transparentCorners: false,
      // cornerDashArray: [2, 2],
      borderColor: colors.tertiary,
      // borderDashArray: [2, 2],
      borderScaleFactor: 0.5,
    };

    InteractiveFabricObject.createControls = () => {
      return {};
    };

    const controls = controlsUtils.createObjectDefaultControls();

    function renderHorizontalControl(ctx, left, top, styleOverride, fabricObject) {
      const width = 16;
      const height = 2;
      ctx.fillStyle = styleOverride.cornerColor || 'blue';
      ctx.fillRect(left - width / 2, top - height / 2, width, height);
    }

    function renderVerticalControl(ctx, left, top, styleOverride, fabricObject) {
      const width = 2;
      const height = 16;
      ctx.fillStyle = styleOverride.cornerColor || 'blue';
      ctx.fillRect(left - width / 2, top - height / 2, width, height);
    }

    const horizontalControlProperties = {
      sizeX: 16,
      sizeY: 8,
      render: renderHorizontalControl,
      actionHandler: controlsUtils.scalingY
    };

    const verticalControlProperties = {
      sizeX: 8,
      sizeY: 16,
      render: renderVerticalControl,
      actionHandler: controlsUtils.scalingX
    };

    InteractiveFabricObject.ownDefaults.controls = {
      ...controls,
      mt: new Control({
        x: 0,
        y: -0.5,
        ...horizontalControlProperties
      }),
      mr: new Control({
        x: 0.5,
        y: 0,
        ...verticalControlProperties
      }),
      mb: new Control({
        x: 0,
        y: 0.5,
        ...horizontalControlProperties
      }),
      ml: new Control({
        x: -0.5,
        y: 0,
        ...verticalControlProperties
      })
    };

  }

  disableDeleting() {

    this.deleteKeyDisabled = true;

  }

  enableDeleting() {

    this.deleteKeyDisabled = false;

  }

  cropImage(imageObject) {

    this.setActiveTool(null);

    ImageToolbar.hide(this.el);

    const onSuccess = () => { this.cropEditor = null; };
    const onCancel = () => { this.cropEditor = null; };
    this.cropEditor = new CropEditor(this, this.fabricCanvas, imageObject, onSuccess, onCancel);

  }

  addFontFamily(fontFamily) {

    const fontFamilies = structuredClone(this.fontFamilies);
    const url = `${import.meta.env.REACT_APP_ASSETS_URL}/assets/fonts/external/${fontFamily}/${fontFamily}-Regular.woff`;
    const fontIndex = fontFamilies.findIndex((font) => font.name === fontFamily);

    if (fontIndex !== -1) {
      return;
    }

    fontFamilies.push({ name: fontFamily, url, weight: 'normal' });
    fontFamilies.push({ name: fontFamily, url: `${import.meta.env.REACT_APP_ASSETS_URL}/assets/fonts/external/${fontFamily}/${fontFamily}-Medium.woff`, style: 'normal', weight: '450' });
    fontFamilies.push({ name: fontFamily, url: `${import.meta.env.REACT_APP_ASSETS_URL}/assets/fonts/external/${fontFamily}/${fontFamily}-Bold.woff`, weight: 'bold' });

    this.fontFamilies = fontFamilies;

  }

}

export default Canvas;